export type BillingPlanId =
  | "guided_digital_access_3m"
  | "guided_digital_access_plus_6m"
  | "guided_digital_access_executive_12m"
  | "monthly_subscription"
  | "deal_win_engine_single_12m"
  | "deal_win_engine_team3_12m"
  | "deal_win_engine_team5_12m"
  | "deal_win_engine_team10_12m";

export type GuidedDigitalAccessPlanId = Extract<
  BillingPlanId,
  | "guided_digital_access_3m"
  | "guided_digital_access_plus_6m"
  | "guided_digital_access_executive_12m"
  | "monthly_subscription"
>;

export type BillingPlanModule = "guided_digital_access" | "deal_win_engine";
export type BillingEntitlementType = "guided_digital_access" | "deal_win_engine";
export type BillingPaymentMode = "subscription";
export type BillingInterval = "monthly" | "every_3_months" | "every_6_months" | "yearly";
export type DepartmentAddonPriceMode = "recurring";
export type CheckoutPurpose = "initial_purchase" | "add_department" | "upgrade" | "renewal" | "reactivation";
export type BillingPlanActionKind =
  | "buy"
  | "current"
  | "renew"
  | "upgrade"
  | "reactivate"
  | "downgrade_unavailable"
  | "monthly_unavailable"
  | "contact_support";

export const BILLING_RENEWAL_WINDOW_DAYS = 14;

export interface CommercialPlan {
  id: BillingPlanId;
  module: BillingPlanModule;
  entitlementType: BillingEntitlementType;
  displayName: string;
  description: string;
  durationMonths?: number;
  contractTermMonths: number;
  autoRenewalTermMonths: number;
  cancellationNoticeDays?: number;
  cancellationNoticeMonths?: number;
  billingInterval: BillingInterval;
  priceNetEur: number;
  currency: "eur";
  includedDepartments: number;
  includedUsers?: number;
  extraDepartmentPriceNetEur: number;
  extraUserPriceNetEur?: number;
  extraDepartmentPriceMode: DepartmentAddonPriceMode;
  paymentMode: BillingPaymentMode;
  isRecommended: boolean;
  isBestValue: boolean;
  renewalDiscountPercent: number;
  reactivationDiscountPercent: 0;
  stripePriceEnvKey: string;
  stripeExtraDepartmentPriceEnvKey?: string;
  stripeExtraUserPriceEnvKey?: string;
}

export interface CheckoutAmount {
  plan: CommercialPlan;
  purpose: CheckoutPurpose;
  extraDepartmentCount: number;
  extraUserCount: number;
  subtotalNetEur: number;
  discountNetEur: number;
  totalNetEur: number;
  renewalDiscountApplied: boolean;
}

export const COMMERCIAL_PLANS: Record<BillingPlanId, CommercialPlan> = {
  guided_digital_access_3m: {
    id: "guided_digital_access_3m",
    module: "guided_digital_access",
    entitlementType: "guided_digital_access",
    displayName: "Guided Digital Access · 3 Monate",
    description: "Einstiegspaket für eine strukturierte Customer Value Calculation.",
    durationMonths: 3,
    contractTermMonths: 3,
    autoRenewalTermMonths: 3,
    cancellationNoticeDays: 14,
    billingInterval: "every_3_months",
    priceNetEur: 3000,
    currency: "eur",
    includedDepartments: 2,
    extraDepartmentPriceNetEur: 300,
    extraDepartmentPriceMode: "recurring",
    paymentMode: "subscription",
    isRecommended: true,
    isBestValue: false,
    renewalDiscountPercent: 15,
    reactivationDiscountPercent: 0,
    stripePriceEnvKey: "STRIPE_PRICE_GUIDED_3M",
    stripeExtraDepartmentPriceEnvKey: "STRIPE_PRICE_GUIDED_3M_EXTRA_DEPARTMENT"
  },
  guided_digital_access_plus_6m: {
    id: "guided_digital_access_plus_6m",
    module: "guided_digital_access",
    entitlementType: "guided_digital_access",
    displayName: "Guided Digital Access Plus · 6 Monate",
    description: "Für mehr Abstimmung, mehr Abteilungen oder längere Umsetzung.",
    durationMonths: 6,
    contractTermMonths: 6,
    autoRenewalTermMonths: 6,
    cancellationNoticeDays: 14,
    billingInterval: "every_6_months",
    priceNetEur: 6300,
    currency: "eur",
    includedDepartments: 3,
    extraDepartmentPriceNetEur: 1200,
    extraDepartmentPriceMode: "recurring",
    paymentMode: "subscription",
    isRecommended: false,
    isBestValue: false,
    renewalDiscountPercent: 15,
    reactivationDiscountPercent: 0,
    stripePriceEnvKey: "STRIPE_PRICE_GUIDED_6M",
    stripeExtraDepartmentPriceEnvKey: "STRIPE_PRICE_GUIDED_6M_EXTRA_DEPARTMENT"
  },
  guided_digital_access_executive_12m: {
    id: "guided_digital_access_executive_12m",
    module: "guided_digital_access",
    entitlementType: "guided_digital_access",
    displayName: "Guided Digital Access Executive · 12 Monate",
    description: "Strategischer Jahreszugang mit bestem Monatswert.",
    durationMonths: 12,
    contractTermMonths: 12,
    autoRenewalTermMonths: 12,
    cancellationNoticeMonths: 3,
    billingInterval: "yearly",
    priceNetEur: 7500,
    currency: "eur",
    includedDepartments: 3,
    extraDepartmentPriceNetEur: 1200,
    extraDepartmentPriceMode: "recurring",
    paymentMode: "subscription",
    isRecommended: false,
    isBestValue: true,
    renewalDiscountPercent: 15,
    reactivationDiscountPercent: 0,
    stripePriceEnvKey: "STRIPE_PRICE_EXECUTIVE_12M",
    stripeExtraDepartmentPriceEnvKey: "STRIPE_PRICE_EXECUTIVE_12M_EXTRA_DEPARTMENT"
  },
  monthly_subscription: {
    id: "monthly_subscription",
    module: "guided_digital_access",
    entitlementType: "guided_digital_access",
    displayName: "Monthly Subscription",
    description: "Monatlicher Zugang mit laufender Subscription-Verwaltung.",
    billingInterval: "monthly",
    contractTermMonths: 1,
    autoRenewalTermMonths: 1,
    cancellationNoticeDays: 14,
    priceNetEur: 2500,
    currency: "eur",
    includedDepartments: 2,
    extraDepartmentPriceNetEur: 150,
    extraDepartmentPriceMode: "recurring",
    paymentMode: "subscription",
    isRecommended: false,
    isBestValue: false,
    renewalDiscountPercent: 15,
    reactivationDiscountPercent: 0,
    stripePriceEnvKey: "STRIPE_PRICE_MONTHLY_SUBSCRIPTION",
    stripeExtraDepartmentPriceEnvKey: "STRIPE_PRICE_MONTHLY_EXTRA_DEPARTMENT"
  },
  deal_win_engine_single_12m: {
    id: "deal_win_engine_single_12m",
    module: "deal_win_engine",
    entitlementType: "deal_win_engine",
    displayName: "Deal Win Engine · Einzelzugang",
    description: "Für Vertriebsverantwortliche, die einzelne Opportunities sauber bewerten und den nächsten Schritt klar priorisieren wollen.",
    durationMonths: 12,
    contractTermMonths: 12,
    autoRenewalTermMonths: 12,
    cancellationNoticeMonths: 3,
    billingInterval: "yearly",
    priceNetEur: 780,
    currency: "eur",
    includedDepartments: 0,
    includedUsers: 1,
    extraDepartmentPriceNetEur: 0,
    extraUserPriceNetEur: 0,
    extraDepartmentPriceMode: "recurring",
    paymentMode: "subscription",
    isRecommended: false,
    isBestValue: false,
    renewalDiscountPercent: 0,
    reactivationDiscountPercent: 0,
    stripePriceEnvKey: "STRIPE_PRICE_DEAL_WIN_SINGLE_12M"
  },
  deal_win_engine_team3_12m: {
    id: "deal_win_engine_team3_12m",
    module: "deal_win_engine",
    entitlementType: "deal_win_engine",
    displayName: "Deal Win Engine · Team 3",
    description: "Für kleine Vertriebsteams, die Deal-Reviews vereinheitlichen und gute Chancen gemeinsam fokussierter steuern wollen.",
    durationMonths: 12,
    contractTermMonths: 12,
    autoRenewalTermMonths: 12,
    cancellationNoticeMonths: 3,
    billingInterval: "yearly",
    priceNetEur: 2160,
    currency: "eur",
    includedDepartments: 0,
    includedUsers: 3,
    extraDepartmentPriceNetEur: 0,
    extraUserPriceNetEur: 720,
    extraDepartmentPriceMode: "recurring",
    paymentMode: "subscription",
    isRecommended: false,
    isBestValue: false,
    renewalDiscountPercent: 0,
    reactivationDiscountPercent: 0,
    stripePriceEnvKey: "STRIPE_PRICE_DEAL_WIN_TEAM3_12M",
    stripeExtraDepartmentPriceEnvKey: "STRIPE_PRICE_DEAL_WIN_TEAM3_EXTRA_USER",
    stripeExtraUserPriceEnvKey: "STRIPE_PRICE_DEAL_WIN_TEAM3_EXTRA_USER"
  },
  deal_win_engine_team5_12m: {
    id: "deal_win_engine_team5_12m",
    module: "deal_win_engine",
    entitlementType: "deal_win_engine",
    displayName: "Deal Win Engine · Team 5",
    description: "Für aktive Vertriebseinheiten, die Pipeline-Qualität, Risiken und nächste Schritte regelmäßig gemeinsam schärfen.",
    durationMonths: 12,
    contractTermMonths: 12,
    autoRenewalTermMonths: 12,
    cancellationNoticeMonths: 3,
    billingInterval: "yearly",
    priceNetEur: 3000,
    currency: "eur",
    includedDepartments: 0,
    includedUsers: 5,
    extraDepartmentPriceNetEur: 0,
    extraUserPriceNetEur: 600,
    extraDepartmentPriceMode: "recurring",
    paymentMode: "subscription",
    isRecommended: false,
    isBestValue: false,
    renewalDiscountPercent: 0,
    reactivationDiscountPercent: 0,
    stripePriceEnvKey: "STRIPE_PRICE_DEAL_WIN_TEAM5_12M",
    stripeExtraDepartmentPriceEnvKey: "STRIPE_PRICE_DEAL_WIN_TEAM5_EXTRA_USER",
    stripeExtraUserPriceEnvKey: "STRIPE_PRICE_DEAL_WIN_TEAM5_EXTRA_USER"
  },
  deal_win_engine_team10_12m: {
    id: "deal_win_engine_team10_12m",
    module: "deal_win_engine",
    entitlementType: "deal_win_engine",
    displayName: "Deal Win Engine · Team 10",
    description: "Für größere Vertriebsorganisationen, die Opportunity-Steuerung skalieren und eine einheitliche Deal-Qualität etablieren wollen.",
    durationMonths: 12,
    contractTermMonths: 12,
    autoRenewalTermMonths: 12,
    cancellationNoticeMonths: 3,
    billingInterval: "yearly",
    priceNetEur: 5400,
    currency: "eur",
    includedDepartments: 0,
    includedUsers: 10,
    extraDepartmentPriceNetEur: 0,
    extraUserPriceNetEur: 540,
    extraDepartmentPriceMode: "recurring",
    paymentMode: "subscription",
    isRecommended: false,
    isBestValue: true,
    renewalDiscountPercent: 0,
    reactivationDiscountPercent: 0,
    stripePriceEnvKey: "STRIPE_PRICE_DEAL_WIN_TEAM10_12M",
    stripeExtraDepartmentPriceEnvKey: "STRIPE_PRICE_DEAL_WIN_TEAM10_EXTRA_USER",
    stripeExtraUserPriceEnvKey: "STRIPE_PRICE_DEAL_WIN_TEAM10_EXTRA_USER"
  }
};

export function getCommercialPlan(planId: string | undefined): CommercialPlan | undefined {
  return planId && planId in COMMERCIAL_PLANS ? COMMERCIAL_PLANS[planId as BillingPlanId] : undefined;
}

export function getCommercialPlans(): CommercialPlan[] {
  return Object.values(COMMERCIAL_PLANS);
}

export function getGuidedDigitalAccessPlans(): CommercialPlan[] {
  return getCommercialPlans().filter((plan) => plan.module === "guided_digital_access");
}

export function getDealWinEnginePlans(): CommercialPlan[] {
  return getCommercialPlans().filter((plan) => plan.module === "deal_win_engine");
}

export function getRecommendedPlan(): CommercialPlan {
  return getGuidedDigitalAccessPlans().find((plan) => plan.isRecommended) ?? COMMERCIAL_PLANS.guided_digital_access_3m;
}

export function getBestValuePlan(): CommercialPlan {
  return getGuidedDigitalAccessPlans().find((plan) => plan.isBestValue) ?? COMMERCIAL_PLANS.guided_digital_access_executive_12m;
}

export function calculateDepartmentAddonPrice(planId: BillingPlanId, extraDepartmentCount: number): number {
  const plan = COMMERCIAL_PLANS[planId];
  return Math.max(0, Math.floor(extraDepartmentCount)) * plan.extraDepartmentPriceNetEur;
}

export function calculateExtraUserAddonPrice(planId: BillingPlanId, extraUserCount: number): number {
  const plan = COMMERCIAL_PLANS[planId];
  return Math.max(0, Math.floor(extraUserCount)) * (plan.extraUserPriceNetEur ?? 0);
}

export function calculateSubscriptionDepartmentPrice(extraDepartmentCount: number): number {
  return calculateDepartmentAddonPrice("monthly_subscription", extraDepartmentCount);
}

export function calculateRenewalDiscount(isBeforeExpiry: boolean, planId: BillingPlanId, subtotalNetEur: number): number {
  if (!isBeforeExpiry) return 0;
  return Math.round(subtotalNetEur * (COMMERCIAL_PLANS[planId].renewalDiscountPercent / 100) * 100) / 100;
}

export function calculateCheckoutAmount(input: {
  planId: BillingPlanId;
  extraDepartmentCount?: number;
  extraUserCount?: number;
  purpose: CheckoutPurpose;
  isBeforeExpiry?: boolean;
}): CheckoutAmount {
  const plan = COMMERCIAL_PLANS[input.planId];
  const extraDepartmentCount = Math.max(0, Math.floor(input.extraDepartmentCount ?? 0));
  const extraUserCount = Math.max(0, Math.floor(input.extraUserCount ?? 0));
  const addonNetEur = plan.module === "deal_win_engine"
    ? calculateExtraUserAddonPrice(input.planId, extraUserCount)
    : calculateDepartmentAddonPrice(input.planId, extraDepartmentCount);
  const subtotalNetEur = input.purpose === "add_department" ? addonNetEur : plan.priceNetEur + addonNetEur;
  const discountNetEur = input.purpose === "renewal" || input.purpose === "upgrade"
    ? calculateRenewalDiscount(Boolean(input.isBeforeExpiry), input.planId, subtotalNetEur)
    : 0;
  return {
    plan,
    purpose: input.purpose,
    extraDepartmentCount,
    extraUserCount,
    subtotalNetEur,
    discountNetEur,
    totalNetEur: Math.max(0, Math.round((subtotalNetEur - discountNetEur) * 100) / 100),
    renewalDiscountApplied: discountNetEur > 0
  };
}

export function planRank(planId: BillingPlanId): number {
  if (planId === "guided_digital_access_3m") return 1;
  if (planId === "guided_digital_access_plus_6m") return 2;
  if (planId === "guided_digital_access_executive_12m") return 3;
  return 0;
}

export function isBillingPlanId(planId: string | undefined): planId is BillingPlanId {
  return Boolean(planId && planId in COMMERCIAL_PLANS);
}

export function isGuidedDigitalAccessPlanId(planId: string | undefined): planId is GuidedDigitalAccessPlanId {
  return Boolean(planId && isBillingPlanId(planId) && COMMERCIAL_PLANS[planId].module === "guided_digital_access");
}

export function isInBillingRenewalWindow(endDate: string | undefined, now = new Date()): boolean {
  if (!endDate) return false;
  const endTime = new Date(endDate).getTime();
  if (!Number.isFinite(endTime)) return false;
  const remainingMs = endTime - now.getTime();
  if (remainingMs <= 0) return true;
  return remainingMs <= BILLING_RENEWAL_WINDOW_DAYS * 24 * 60 * 60 * 1000;
}

export function getBillingPlanAction(input: {
  targetPlanId: string;
  currentPlanId?: string;
  productiveAccessAllowed: boolean;
  canReactivate: boolean;
  entitlementStatus?: string;
  periodEnd?: string;
  now?: Date;
}): BillingPlanActionKind {
  const targetPlan = getCommercialPlan(input.targetPlanId);
  if (!targetPlan) return "contact_support";
  if (targetPlan.module !== "guided_digital_access") return "contact_support";
  const currentPlanId = isBillingPlanId(input.currentPlanId) ? input.currentPlanId : undefined;
  const renewalWindowOpen = isInBillingRenewalWindow(input.periodEnd, input.now);

  if (input.productiveAccessAllowed && currentPlanId === targetPlan.id) return renewalWindowOpen ? "renew" : "current";
  if (input.productiveAccessAllowed && targetPlan.id === "monthly_subscription" && !renewalWindowOpen) return "monthly_unavailable";
  if (input.productiveAccessAllowed && targetPlan.id === "monthly_subscription" && renewalWindowOpen) return "renew";

  if (input.productiveAccessAllowed && currentPlanId) {
    const currentRank = planRank(currentPlanId);
    const targetRank = planRank(targetPlan.id);
    if (targetRank > currentRank) return "upgrade";
    if (targetRank < currentRank) return "downgrade_unavailable";
    return "renew";
  }

  if (input.productiveAccessAllowed) return targetPlan.id === "monthly_subscription" && !renewalWindowOpen ? "monthly_unavailable" : "renew";
  if (input.canReactivate) return "reactivate";

  if (!input.entitlementStatus || input.entitlementStatus === "inactive" || input.entitlementStatus === "pending_checkout" || input.entitlementStatus === "payment_pending" || input.entitlementStatus === "payment_failed") {
    return "buy";
  }

  return "contact_support";
}
